import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import type { ButtonProps } from "@mui/material/Button";
import type { DialogProps } from "@mui/material/Dialog";
import LoadingButton from "@mui/lab/LoadingButton";
import { X } from "@phosphor-icons/react";

export interface ConfirmDialogProps {
  /** Controls dialog visibility. */
  open: boolean;
  /** Dialog title text. */
  title: string;
  /** Dialog subtitle text. */
  subtitle?: string;
  /**
   * Body content. Accepts any ReactNode — use Typography or a custom layout.
   * Rendered inside DialogContent.
   */
  children: React.ReactNode;
  /** Label for the primary (confirm) button. */
  confirmLabel: string;
  /** MUI color for the confirm button. @default "primary" */
  confirmColor?: ButtonProps["color"];
  /** Label for the cancel button. @default "Cancel" */
  cancelLabel?: string;
  /**
   * Called when the X icon or the dialog backdrop is clicked.
   * Represents a neutral dismiss (no explicit choice made).
   */
  onClose: () => void;
  /**
   * Called when the cancel button is clicked.
   * Falls back to `onClose` when not provided.
   */
  onCancel?: () => void;
  /** Called when the confirm button is clicked. */
  onConfirm: () => void;
  /** Shows a loading spinner on the confirm button and disables all actions. */
  isLoading?: boolean;
  /** Disables the confirm button independently of `isLoading`. */
  confirmDisabled?: boolean;
  /**
   * Extra content rendered at the start of the actions row, left-aligned and
   * separated from the buttons — e.g. a "Create more" checkbox.
   */
  actionsStart?: React.ReactNode;
  /** Max width of the dialog. @default "sm" */
  maxWidth?: DialogProps["maxWidth"];
}

/**
 * Generic confirmation dialog with a flexible body slot, customisable button
 * labels/colors, and separate handlers for close (neutral dismiss), cancel,
 * and confirm actions.
 *
 * Use this as a base for feature-specific dialogs (e.g. DeleteTargetDialog,
 * DraftResultDialog) rather than duplicating the dialog shell.
 *
 * @param props
 * @param props.open           - Controls dialog visibility
 * @param props.title          - Dialog heading
 * @param props.children       - Flexible body content
 * @param props.confirmLabel   - Primary action button label
 * @param props.confirmColor   - MUI color for the confirm button (default "primary")
 * @param props.cancelLabel    - Cancel button label (default "Cancel")
 * @param props.onClose        - X icon / backdrop click handler
 * @param props.onCancel       - Cancel button handler (falls back to onClose)
 * @param props.onConfirm      - Confirm button handler
 * @param props.isLoading      - Shows spinner and disables actions
 * @param props.maxWidth       - Dialog max width (default "sm")
 * @param props.actionsStart   - Extra left-aligned content in the actions row
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  subtitle,
  children,
  confirmLabel,
  confirmColor = "primary",
  cancelLabel = "Cancel",
  onClose,
  onCancel,
  onConfirm,
  isLoading = false,
  confirmDisabled = false,
  maxWidth = "sm",
  actionsStart,
}) => {
  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const handleCancel = () => {
    if (isLoading) return;
    (onCancel ?? onClose)();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={maxWidth} fullWidth>
      {/* Title row */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 7,
          py: 5,
        }}
      >
        <Typography component="span" variant="title.medium" fontWeight={500}>
          {title}
          {/* Subtitle */}
          {subtitle && (
            <Typography variant="body.small" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Typography>
        <IconButton
          size="small"
          aria-label="Close dialog"
          onClick={handleClose}
          disabled={isLoading}
          sx={{ color: "text.secondary" }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      {/* Body */}
      <DialogContent sx={{ px: 7, py: 0 }}>{children}</DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 7, py: 5, gap: 2 }}>
        {/* Optional left-aligned slot (e.g. a "Create more" checkbox). */}
        {actionsStart ? (
          <Box
            component="fieldset"
            disabled={isLoading}
            sx={{ m: 0, p: 0, border: 0, minInlineSize: "auto" }}
          >
            {actionsStart}
          </Box>
        ) : null}

        <Button
          variant="contained"
          color="secondary"
          onClick={handleCancel}
          disabled={isLoading}
          aria-label={cancelLabel}
        >
          {cancelLabel}
        </Button>

        <LoadingButton
          variant="contained"
          color={confirmColor}
          loading={isLoading}
          disabled={isLoading || confirmDisabled}
          onClick={onConfirm}
          aria-label={confirmLabel}
        >
          {confirmLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
